import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { MovieCard } from '@/components/MovieCard';
import { MovieFilters } from '@/components/MovieFilters';
import Pagination from '@/components/Pagination';
import { Loader2, Clock, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { TrialExpiredModal } from '@/components/TrialExpiredModal';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MOVIES_PER_PAGE = 50;

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'tokrav_latest');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedGenre, setSelectedGenre] = useState(() => searchParams.get('genre') || '');
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam) : 1;
  });
  const [autoScroll, setAutoScroll] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalMovies, setTotalMovies] = useState(0);
  const { user, token} = useAuth();
  const loadingRef = useRef(false);
  const isInitialMount = useRef(true);
  
  const totalPages = Math.ceil(totalMovies / MOVIES_PER_PAGE);

  // Fetch total count of movies
  const fetchMoviesCount = async () => {
    try {
      let url = `${API}/movies-count`;
      const params = [];
      
      if (selectedGenre) {
        params.push(`genre=${encodeURIComponent(selectedGenre)}`);
      }
      
      if (sortBy) {
        params.push(`sort_by=${sortBy}`);
      }
      
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await axios.get(url);
      setTotalMovies(response.data.count);
    } catch (e) {
    }
  };

  const fetchMovies = async (page, append = false) => {
    if (loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);
      
      const skip = (page - 1) * MOVIES_PER_PAGE;
      let url = `${API}/movies?limit=${MOVIES_PER_PAGE}&skip=${skip}&sort_by=${sortBy}&order=${sortOrder}`;
      
      if (selectedGenre) {
        url += `&genre=${encodeURIComponent(selectedGenre)}`;
      }
      
      const response = await axios.get(url);
      
      if (append) {
        setMovies(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMovies = response.data.filter(m => !existingIds.has(m.id));
          return [...prev, ...newMovies];
        });
        setHasMore(response.data.length === MOVIES_PER_PAGE);
      } else {
        setMovies(response.data);
        if (!autoScroll) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (e) {
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const loadFavorites = async () => {
    if (user && token) {
      try {
        const response = await axios.get(`${API}/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const ids = (response.data.movies || []).map(m => m.id);
        setFavoriteIds(ids);
      } catch (err) {
      }
    }
  };

  const handleFavoriteToggle = () => {
    loadFavorites();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    
    // Build URL params with all current filters
    const params = {};
    if (newPage > 1) params.page = newPage;
    if (selectedGenre) params.genre = selectedGenre;
    if (sortBy !== 'tokrav_latest') params.sort = sortBy;
    
    setSearchParams(params);
  };

  const handleFilterChange = ({ sortBy: newSort, sortOrder: newOrder, genre }) => {
    setSortBy(newSort);
    setSortOrder(newOrder);
    setSelectedGenre(genre);
    setCurrentPage(1);
    
    // Build URL params with new filters
    const params = {};
    if (genre) params.genre = genre;
    if (newSort !== 'tokrav_latest') params.sort = newSort;
    
    setSearchParams(params);
  };

  // Load favorites on mount
  useEffect(() => {
    loadFavorites();
  }, [user, token]);

  // Master effect: Load movies when page or filters change
  useEffect(() => {
    fetchMovies(currentPage, false);
    fetchMoviesCount();
  }, [currentPage, sortBy, sortOrder, selectedGenre]);

  // Read all params from URL on mount only
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const genreParam = searchParams.get('genre');
    const sortParam = searchParams.get('sort');
    
    if (pageParam) {
      const pageNumber = parseInt(pageParam);
      if (pageNumber > 0 && pageNumber !== currentPage) {
        setCurrentPage(pageNumber);
      }
    }
    
    if (genreParam && genreParam !== selectedGenre) {
      setSelectedGenre(genreParam);
    }
    
    if (sortParam && sortParam !== sortBy) {
      setSortBy(sortParam);
    }
  }, []); // Run once on mount

  // When page changes (normal pagination)
  useEffect(() => {
    if (!autoScroll && currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, autoScroll]);

  // When auto-scroll mode changes
  useEffect(() => {
    if (autoScroll) {
      // Don't reset movies or scroll to top
      // Just enable infinite scroll for current position
      setHasMore(true);
    }
  }, [autoScroll]);

  // Load favorites when user changes
  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user]);

  // Infinite scroll - SEPARATE useEffect at top level
  useEffect(() => {
    if (!autoScroll) return;
    
    const handleScroll = () => {
      if (loadingRef.current || !hasMore) return;
      if (currentPage >= totalPages) return;
      
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      
      if (scrollTop + windowHeight >= docHeight - 500) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchMovies(nextPage, true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [autoScroll, currentPage, totalPages, hasMore]);

  const filters = [
    { id: 'latest', label: 'Derniers ajouts', icon: Clock },
    { id: 'vote_average', label: 'Les mieux notés', icon: TrendingUp },
    { id: 'title', label: 'A-Z', icon: null },
  ];

  return (
    <div className="min-h-screen bg-bg-primary">
      <TrialExpiredModal />
      <Navigation />
      
      <div className="pt-24 px-4 md:px-8 lg:px-12 xl:px-16 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bebas font-bold text-white mb-2">
                🎬 FILMS
              </h1>
              <p className="text-text-secondary font-inter text-sm md:text-base">
                {autoScroll 
                  ? `${movies.length} film(s) chargé(s) • Total: ${totalMovies} films`
                  : `${movies.length} film(s) sur cette page • Total: ${totalMovies} films • Page ${currentPage}/${totalPages}`
                }
              </p>
            </div>
            
            {/* Auto-scroll toggle */}
            <div className="flex items-center gap-3 bg-white/5 px-4 py-3 rounded-lg border border-white/10">
              <input
                type="checkbox"
                id="autoScroll"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="w-5 h-5 rounded border-white/30 bg-bg-tertiary text-netflix-red focus:ring-netflix-red cursor-pointer"
              />
              <label htmlFor="autoScroll" className="text-white font-work font-semibold text-sm cursor-pointer select-none">
                🔄 Défilement automatique
              </label>
            </div>
          </div>
        </div>

        {/* Filtres modernes */}
        <MovieFilters 
          onFilterChange={handleFilterChange}
          currentSort={sortBy}
          currentGenre={selectedGenre}
        />

        {loading && movies.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-netflix-red animate-spin" />
          </div>
        ) : movies.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {movies.map((movie) => (
                <MovieCard 
                  key={movie.id} 
                  movie={movie}
                  isFavorite={favoriteIds.includes(movie.id)}
                  onFavoriteToggle={handleFavoriteToggle}
                />
              ))}
            </div>
            
            {/* Loading More (auto-scroll) */}
            {loading && autoScroll && (
              <div className="flex items-center justify-center py-8 mt-6">
                <Loader2 className="w-8 h-8 text-netflix-red animate-spin mr-3" />
                <p className="text-white font-work">Chargement de plus de films...</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <p className="text-xl md:text-2xl text-text-secondary font-work">
              Aucun film disponible pour le moment
            </p>
          </div>
        )}
        
        {/* Pagination (only when auto-scroll OFF) */}
        {!autoScroll && totalPages > 1 && movies.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}
